import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/lib/api';
import { SecureStorage } from '@/lib/sessionManager';
import { Mail, Clock, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Falcon SVG icon
const FalconIcon = ({ color = '#059669', size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 4C28 4 20 10 16 18C12 26 12 32 14 36C16 40 18 42 22 44L20 52L24 50L26 54L30 50L32 56L34 50L38 54L40 50L44 52L42 44C46 42 48 40 50 36C52 32 52 26 48 18C44 10 36 4 32 4Z" fill={color} opacity="0.15" stroke={color} strokeWidth="2" strokeLinejoin="round"/>
    <path d="M32 12C29 12 24 16 22 22C20 28 21 32 24 35" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M32 12C35 12 40 16 42 22C44 28 43 32 40 35" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="26" cy="24" r="2" fill={color}/>
    <circle cx="38" cy="24" r="2" fill={color}/>
    <path d="M29 30L32 34L35 30" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [oauthBanner, setOauthBanner] = useState(null);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
  const [branding, setBranding] = useState({
    title: 'Baobab Connect',
    subtitle: 'Falcon',
    background_url: '',
    background_type: 'image',
    logo_url: '',
    logo_type: 'falcon',
    primary_color: '#059669',
    secondary_color: '#f59e0b',
  });
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Load branding + OAuth status
  useEffect(() => {
    const init = async () => {
      try {
        const [brandingResp, oauthResp] = await Promise.allSettled([
          fetch(`${API_URL}/api/branding/login`).then(r => r.json()),
          api.get('/auth/oauth-status'),
        ]);
        if (brandingResp.status === 'fulfilled') setBranding(prev => ({ ...prev, ...brandingResp.value }));
        if (oauthResp.status === 'fulfilled') {
          const d = oauthResp.value.data;
          setMicrosoftEnabled(d.microsoft_configured && d.microsoft_enabled);
        }
      } catch {}
    };
    init();
  }, []);

  // Handle OAuth redirect params
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success');
    const oauthError = searchParams.get('oauth_error');
    const oauthStatus = searchParams.get('oauth_status');
    const token = searchParams.get('token');
    const userId = searchParams.get('user_id');
    const message = searchParams.get('message');
    const email = searchParams.get('email');
    const reason = searchParams.get('reason');

    if (reason) {
      const messages = {
        session_expired: 'Votre session a expire. Veuillez vous reconnecter.',
        inactivity: "Deconnecte pour inactivite.",
        no_session: 'Veuillez vous connecter.',
      };
      if (messages[reason]) toast.warning(messages[reason], { duration: 5000 });
    }

    if (oauthSuccess === 'true' && token && userId) {
      handleOAuthSuccess(token);
      return;
    }

    if (oauthError) {
      const errorMessages = {
        auth_failed: "Echec de l'authentification Microsoft",
        graph_error: 'Impossible de recuperer le profil Microsoft',
        no_email: 'Email non disponible dans le profil Microsoft',
        domain_denied: message || 'Domaine non autorise',
        account_deleted: 'Ce compte a ete supprime',
        account_inactive: 'Compte desactive',
        invalid_token: 'Lien de verification invalide ou expire',
        token_expired: 'Lien de verification expire',
      };
      setOauthBanner({ type: 'error', message: errorMessages[oauthError] || message || "Erreur d'authentification" });
    }

    if (oauthStatus) {
      const statusBanners = {
        email_pending: { type: 'info', message: `Email de verification envoye a ${email || 'votre adresse'}.`, email },
        pending_approval: { type: 'warning', message: "Email verifie. En attente d'approbation administrateur." },
        approved: { type: 'success', message: 'Compte approuve ! Connectez-vous avec Microsoft.' },
      };
      if (statusBanners[oauthStatus]) setOauthBanner(statusBanners[oauthStatus]);
    }
  }, [searchParams]);

  const handleOAuthSuccess = async (token) => {
    try {
      SecureStorage.setToken(token);
      const resp = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      SecureStorage.setUser(resp.data);
      setUser(resp.data);
      toast.success('Connexion reussie via Microsoft');
      navigate('/');
    } catch {
      toast.error('Erreur lors de la connexion automatique');
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API_URL}/api/auth/microsoft/login`;
  };

  const handleResendVerification = async () => {
    if (!oauthBanner?.email) return;
    try {
      await api.post('/auth/resend-verification', { email: oauthBanner.email });
      toast.success('Email de verification renvoye');
    } catch { toast.error('Erreur lors du renvoi'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', formData);
      const { access_token, user } = response.data;
      SecureStorage.setToken(access_token);
      SecureStorage.setUser(user);
      setUser(user);
      toast.success('Connexion reussie', { description: `Bienvenue ${user.nom}` });
      navigate('/');
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Erreur de connexion');
      toast.error('Connexion echouee', { description: errorMessage, duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const bannerStyles = {
    info: 'bg-blue-500/10 border-blue-400/30 text-blue-100',
    warning: 'bg-amber-500/10 border-amber-400/30 text-amber-100',
    success: 'bg-green-500/10 border-green-400/30 text-green-100',
    error: 'bg-red-500/10 border-red-400/30 text-red-100',
  };
  const bannerIcons = {
    info: <Mail className="h-5 w-5 flex-shrink-0" />,
    warning: <Clock className="h-5 w-5 flex-shrink-0" />,
    success: <CheckCircle className="h-5 w-5 flex-shrink-0" />,
    error: <XCircle className="h-5 w-5 flex-shrink-0" />,
  };

  const primaryColor = branding.primary_color || '#059669';

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      {branding.background_type === 'image' && branding.background_url ? (
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${branding.background_url})` }}>
          <div className="absolute inset-0 bg-black/40" />
        </div>
      ) : branding.background_type === 'gradient' ? (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color || '#1f2937'})` }} />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900" />
      )}

      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-md relative z-10" data-testid="login-card">
        <CardHeader className="space-y-3 text-center pb-4">
          <div className="mx-auto">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-14 mx-auto object-contain" />
            ) : (
              <FalconIcon color={primaryColor} size={56} />
            )}
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">{branding.title}</CardTitle>
          <CardDescription className="text-base text-gray-500">{branding.subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* OAuth Banner */}
          {oauthBanner && (
            <div className={`flex items-start gap-3 p-3 rounded-lg border ${
              oauthBanner.type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800' :
              oauthBanner.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
              oauthBanner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
              'bg-red-50 border-red-200 text-red-800'
            }`} data-testid="oauth-banner">
              {bannerIcons[oauthBanner.type]}
              <div className="flex-1 text-sm">
                <p>{oauthBanner.message}</p>
                {oauthBanner.type === 'info' && oauthBanner.email && (
                  <button onClick={handleResendVerification} className="mt-1 underline text-xs opacity-80 hover:opacity-100" data-testid="resend-verification-btn">
                    Renvoyer l'email
                  </button>
                )}
              </div>
              <button onClick={() => setOauthBanner(null)} className="opacity-50 hover:opacity-100">
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Microsoft Login */}
          {microsoftEnabled && (
            <>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 flex items-center justify-center gap-3 border-gray-300 hover:bg-gray-50"
                onClick={handleMicrosoftLogin}
                data-testid="microsoft-login-btn"
              >
                <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Se connecter avec Microsoft (interne)
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white/95 px-2 text-gray-500">ou</span></div>
              </div>
            </>
          )}

          {/* Classic Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email ou identifiant</Label>
              <Input id="email" type="text" placeholder="Login" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" placeholder="--------" value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="password-input" className="h-11" />
            </div>
            <Button type="submit" className="w-full h-11" style={{ backgroundColor: primaryColor }} disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
